import { useEffect, useMemo, useState } from "react";
import { PlayCircle, Trash2, Upload } from "lucide-react";
import { BackButton } from "../../../../components/button/back-button";
import { LoadingButton } from "../../../../components/button/loading-button";
import { ConfirmationModal } from "../../../../components/confirmation-modal";
import { useAuth } from "../../../../hooks/use-auth";
import { useToast } from "../../../../contexts/toast/toast-context";
import api from "../../../../services/api";
import { getApiErrorMessage } from "../../../../utils";
import type { TrainingVideo } from "../../../../types/training-video";
import { FileUpload } from "../../../../components/file-upload";

const MAX_VIDEO_SIZE_BYTES = 100 * 1024 * 1024;

const formatFileSize = (value: number): string => {
  const size = Number(value);
  if (!Number.isFinite(size) || size <= 0) return "0 B";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export default function TrainingVideosPage() {
  const { user } = useAuth();
  const { success, error, warn } = useToast();

  const isAdmin = user?.role === "ADMIN";

  const [videos, setVideos] = useState<TrainingVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [selectedVideo, setSelectedVideo] = useState<TrainingVideo | null>(
    null,
  );
  const [videoToDelete, setVideoToDelete] = useState<TrainingVideo | null>(
    null,
  );

  const [caption, setCaption] = useState("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const hasVideos = useMemo(() => videos.length > 0, [videos.length]);

  const loadVideos = async () => {
    try {
      setIsLoading(true);
      const response = await api.get<TrainingVideo[]>("/training-videos");
      setVideos(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      error(getApiErrorMessage(err));
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadVideos();
  }, []);

  const handleUpload = async () => {
    if (!isAdmin || isUploading) return;

    const normalizedCaption = caption.trim();
    if (!normalizedCaption) {
      warn("Informe a legenda do vídeo.");
      return;
    }

    if (!pendingFile) {
      warn("Selecione um vídeo para upload.");
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("video", pendingFile);
      formData.append("caption", normalizedCaption);

      await api.post("/training-videos", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      success("Vídeo de treinamento cadastrado com sucesso.");
      setCaption("");
      setPendingFile(null);
      await loadVideos();
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!videoToDelete || isDeleting) return;

    try {
      setIsDeleting(true);
      await api.delete(`/training-videos/${videoToDelete.id}`);
      setVideoToDelete(null);
      success("Vídeo removido com sucesso.");
      await loadVideos();
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="app-page">
      <BackButton />
      <div className="mb-6">
        <h1 className="app-page-title">Treinamentos</h1>
        <p className="app-page-subtitle">
          Clique em um vídeo para abrir o player e iniciar a reprodução.
        </p>
      </div>

      {isAdmin && (
        <div className="app-card mb-8 p-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">
            Novo treinamento
          </h2>
          <div className="space-y-4">
            <div>
              <label className="form-label">Legenda</label>
              <input
                className="form-input"
                placeholder="Ex: Como configurar lembretes de consulta"
                value={caption}
                maxLength={160}
                onChange={(event) => setCaption(event.target.value)}
                disabled={isUploading}
              />
            </div>

            <FileUpload files={pendingFile ? [pendingFile] : []} onFilesChange={(files) => setPendingFile(files[0] ?? null)} accept="video/*" acceptLabel="Vídeos" multiple={false} maxFiles={1} maxSizeBytes={MAX_VIDEO_SIZE_BYTES} disabled={isUploading} uploading={isUploading} onValidationError={error} emptyLabel="Nenhum vídeo selecionado." />
          </div>

          <div className="mt-4 flex justify-end">
            <LoadingButton
              className="btn btn-primary flex items-center gap-2"
              onClick={handleUpload}
              isLoading={isUploading}
              loadingLabel="Enviando"
            >
              <Upload className="h-4 w-4" />
              Cadastrar vídeo
            </LoadingButton>
          </div>
        </div>
      )}

      {isLoading && (
        <p className="app-page-subtitle">Carregando treinamentos...</p>
      )}

      {!isLoading && !hasVideos && (
        <div className="app-card p-8 text-center">
          <p className="text-slate-700">Nenhum vídeo cadastrado no momento.</p>
        </div>
      )}

      {!isLoading && hasVideos && (
        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {videos.map((video) => (
            <article
              key={video.id}
              className="app-card overflow-hidden p-0 cursor-pointer"
              onClick={() => setSelectedVideo(video)}
            >
              <div className="relative bg-slate-900">
                <video
                  src={video.videoUrl}
                  className="h-52 w-full object-cover"
                  preload="metadata"
                  muted
                />
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/20">
                  <PlayCircle className="h-14 w-14 text-white/95" />
                </div>
              </div>

              <div className="p-4">
                <p className="text-sm font-medium text-slate-900">
                  {video.caption}
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  {video.fileName} • {formatFileSize(video.fileSize)}
                </p>

                {isAdmin && (
                  <div className="mt-4">
                    <button
                      className="btn-link-danger flex items-center gap-1"
                      onClick={(event) => {
                        event.stopPropagation();
                        setVideoToDelete(video);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                      Excluir
                    </button>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {selectedVideo && (
        <div
          className="modal-overlay !bg-slate-950/80 p-4"
          onClick={() => setSelectedVideo(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-4xl rounded-2xl bg-black p-3 shadow-soft"
            onClick={(event) => event.stopPropagation()}
          >
            <video
              src={selectedVideo.videoUrl}
              controls
              autoPlay
              className="w-full max-h-[75vh] rounded-lg"
            />
            <div className="px-1 pt-3 pb-1">
              <p className="text-sm font-medium text-slate-100">
                {selectedVideo.caption}
              </p>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={!!videoToDelete}
        title="Excluir vídeo"
        description="Tem certeza que deseja excluir este treinamento?"
        onCancel={() => setVideoToDelete(null)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        confirmLabel="Excluir"
      />
    </div>
  );
}
